/** Admin — manage CCS Spotlight stories and review nominations. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ExternalLink, Star } from "lucide-react";
import { CATEGORIES, categoryBadge, formatSpotlightDate } from "@/components/spotlight/spotlightUtils";
import HeroImagesManager from "@/components/spotlight/HeroImagesManager";
import ImageUploadField from "@/components/ImageUploadField";
import SignatureSetting from "@/components/SignatureSetting";
import RichTextEditor from "@/components/RichTextEditor";

const EMPTY = {
  honoree_name: "", organization: "", category: "Customer Service", title: "",
  summary: "", image_url: "", what_happened: "", why_this_matters: "",
  ccs_lesson: "", ccs_reflection: "", published: false,
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">{label}</div>
    {children}
  </div>
);

const SpotlightFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [open, editing]);

  const save = async () => {
    if (!form.honoree_name || !form.summary || !form.what_happened || !form.why_this_matters || !form.ccs_lesson || !form.ccs_reflection) {
      toast.error("Please complete the honoree, summary and all four story sections.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/spotlights/${editing.id}`, form);
        toast.success("Spotlight updated.");
      } else {
        await api.post("/spotlights", form);
        toast.success("Spotlight created.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF5] border-[#EBE3D2]" data-testid="spotlight-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2A1810]">
            {editing?.id ? "Edit Spotlight" : "New Spotlight"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Honoree name *">
              <Input value={form.honoree_name} onChange={(e) => setF("honoree_name", e.target.value)} data-testid="spotlight-form-honoree" />
            </Field>
            <Field label="Company / organization">
              <Input value={form.organization} onChange={(e) => setF("organization", e.target.value)} data-testid="spotlight-form-organization" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                <SelectTrigger data-testid="spotlight-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Headline (optional)">
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="e.g. The Associate Who Didn't Wait to Be Asked" data-testid="spotlight-form-title" />
            </Field>
          </div>
          <Field label="One-sentence summary *">
            <Textarea rows={2} value={form.summary} onChange={(e) => setF("summary", e.target.value)} data-testid="spotlight-form-summary" />
          </Field>
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setF("image_url", url)}
            label="Feature image"
            testid="spotlight-form-image"
          />
          <Field label="What Happened *">
            <RichTextEditor value={form.what_happened} onChange={(v) => setF("what_happened", v)} minHeight={150} testid="spotlight-form-what-happened" />
          </Field>
          <Field label="Why This Matters *">
            <RichTextEditor value={form.why_this_matters} onChange={(v) => setF("why_this_matters", v)} minHeight={110} testid="spotlight-form-why-matters" />
          </Field>
          <Field label="The CCS Lesson *">
            <RichTextEditor value={form.ccs_lesson} onChange={(v) => setF("ccs_lesson", v)} minHeight={90} testid="spotlight-form-lesson" />
          </Field>
          <Field label="CCS Reflection * (short quote, signed by you)">
            <Textarea rows={2} value={form.ccs_reflection} onChange={(e) => setF("ccs_reflection", e.target.value)} data-testid="spotlight-form-reflection" />
          </Field>
          <div className="flex items-center justify-between bg-white border border-[#EBE3D2] rounded-xl px-4 py-3">
            <div className="text-sm font-medium text-[#2A1810]">Published (visible to the public)</div>
            <Switch checked={form.published} onCheckedChange={(v) => setF("published", v)} data-testid="spotlight-form-published" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="spotlight-form-save">
            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create Spotlight"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SpotlightAdminPage() {
  const [spotlights, setSpotlights] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/spotlights").then((r) => setSpotlights(r.data.spotlights || [])).catch(() => {});
    api.get("/spotlight-nominations").then((r) => setNominations(r.data.nominations || [])).catch(() => {});
  };
  useEffect(load, []);

  const togglePublish = async (s) => {
    try {
      await api.put(`/spotlights/${s.id}`, { published: !s.published });
      toast.success(s.published ? "Moved to draft." : "Published.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the spotlight for "${s.honoree_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/spotlights/${s.id}`);
      toast.success("Spotlight deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const setNominationStatus = async (n, status) => {
    try {
      await api.put(`/spotlight-nominations/${n.id}`, { status });
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const newCount = nominations.filter((n) => n.status === "new").length;

  return (
    <div className="space-y-6" data-testid="spotlight-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#2A1810]">CCS Spotlight</h1>
          <p className="text-sm text-[#6B5944] mt-1">
            Publish stories celebrating excellence · <a href="/spotlight" target="_blank" rel="noopener noreferrer" className="text-[#C28A2E] hover:underline">view public page</a>
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="new-spotlight-button">
          <Plus size={16} className="mr-1.5" /> New Spotlight
        </Button>
      </div>

      <Tabs defaultValue="stories">
        <TabsList className="bg-[#F6F3EB]">
          <TabsTrigger value="stories" data-testid="tab-stories">Stories ({spotlights.length})</TabsTrigger>
          <TabsTrigger value="nominations" data-testid="tab-nominations">
            Nominations ({nominations.length}){newCount > 0 && <span className="ml-1.5 bg-[#C28A2E] text-white text-[10px] px-1.5 py-0.5 rounded-full">{newCount} new</span>}
          </TabsTrigger>
          <TabsTrigger value="hero-images" data-testid="tab-hero-images">Hero Images</TabsTrigger>
        </TabsList>

        <TabsContent value="stories" className="space-y-3 mt-4">
          {spotlights.length === 0 && (
            <div className="py-16 text-center text-[#6B5944] border border-dashed border-[#EBE3D2] rounded-2xl">
              No spotlights yet — create your first story.
            </div>
          )}
          {spotlights.map((s) => (
            <div key={s.id} className="ccs-card bg-white border border-[#EBE3D2] rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-spotlight-row-${s.id}`}>
              {s.image_url && <img src={s.image_url} alt="" className="w-16 h-12 object-cover rounded-md hidden sm:block" />}
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#2A1810]">{s.title || s.honoree_name}</span>
                  {s.published ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Published</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[#6B5944]">Draft</Badge>
                  )}
                </div>
                <div className="text-xs text-[#6B5944] mt-1 flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${categoryBadge(s.category)}`}>{s.category}</span>
                  <span>{s.honoree_name}{s.organization ? ` · ${s.organization}` : ""}</span>
                  {s.published_at && <span>· {formatSpotlightDate(s.published_at)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {s.published && (
                  <a href={`/spotlight/${s.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#6B5944] hover:text-[#C28A2E]" title="View" data-testid={`view-spotlight-${s.id}`}>
                    <ExternalLink size={15} />
                  </a>
                )}
                <Button variant="outline" size="sm" onClick={() => togglePublish(s)} data-testid={`toggle-publish-${s.id}`}>
                  <Star size={14} className="mr-1" /> {s.published ? "Unpublish" : "Publish"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setEditing(s); setFormOpen(true); }} data-testid={`edit-spotlight-${s.id}`}>
                  <Pencil size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(s)} className="text-red-600 hover:text-red-700" data-testid={`delete-spotlight-${s.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="nominations" className="space-y-3 mt-4">
          {nominations.length === 0 && (
            <div className="py-16 text-center text-[#6B5944] border border-dashed border-[#EBE3D2] rounded-2xl">
              No nominations yet. They'll appear here when visitors submit the form on the public page.
            </div>
          )}
          {nominations.map((n) => (
            <div key={n.id} className="bg-white border border-[#EBE3D2] rounded-xl p-5" data-testid={`nomination-row-${n.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#2A1810]">{n.nominee_name}</span>
                    {n.nominee_organization && <span className="text-sm text-[#6B5944]">· {n.nominee_organization}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${categoryBadge(n.category)}`}>{n.category}</span>
                    {n.status === "new" && <Badge className="bg-[#C28A2E]/15 text-[#9A6B1E] hover:bg-[#C28A2E]/15">New</Badge>}
                  </div>
                  <p className="text-sm text-[#2A1810]/80 mt-2 whitespace-pre-line">{n.story}</p>
                  <p className="text-xs text-[#6B5944] mt-2">
                    Nominated by {n.nominator_name} ({n.nominator_email}) · {formatSpotlightDate(n.created_at)}
                  </p>
                </div>
                <Select value={n.status} onValueChange={(v) => setNominationStatus(n, v)}>
                  <SelectTrigger className="w-32" data-testid={`nomination-status-${n.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="hero-images" className="mt-4">
          <HeroImagesManager />
        </TabsContent>
      </Tabs>

      <SignatureSetting />

      <SpotlightFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} onSaved={load} />
    </div>
  );
}
